import { api } from '@udm/contracts';
import { useQuery } from '@tanstack/react-query';
import { useMemo, useState, type PointerEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { canAny, isClientUser, request } from '../../api/client';
import { useMe } from '../auth/session';
import { i18n } from '../../i18n';
import { deviceStatic, useStaticVersion } from '../../realtime/deviceStatic';
import { Card } from '../../ui/components';
import { formatNumber } from '../../ui/format';
import { useWidth } from '../../ui/useWidth';

/**
 * "Online over time" (5-minute samples per client, GET /api/v1/stats/presence?by=client). Quick first version, own
 * SVG drawn at the real pixel size (text never scales): total online/offline (online solid, offline dashed + direct
 * labels — identity never by colour alone), crosshair tooltip, then one row per client with its current count and a
 * sparkline; a click on a client focuses the main chart on it. Refreshed every minute over REST.
 * TODO(charts): shared chart component/library, rollups for long ranges, table view, export.
 */
type Point = api.PresenceHistory['points'][number];
const RANGES = [6, 24, 24 * 7] as const;
const H = 240;
const M = { top: 12, right: 56, bottom: 26, left: 48 };

const niceMax = (v: number) => {
  if (v <= 5) return 5;
  const p = 10 ** Math.floor(Math.log10(v));
  return [1, 2, 2.5, 5, 10].map((m) => m * p).find((c) => c >= v)!;
};

export function PresenceChart() {
  const { t } = useTranslation();
  const [client, setClient] = useState('');
  const [protocol, setProtocol] = useState('');
  const me = useMe().data;
  const ops = canAny(me, 'operations.view');
  // a client's own accounts see one client: no "all clients" in the title
  const clientUser = isClientUser(me);
  const [hours, setHours] = useState<number>(24);
  const staticVersion = useStaticVersion((s) => s.version);
  const protocols = useMemo(
    () =>
      [
        ...new Set([...deviceStatic.values()].map((d) => d.protocol).filter((p): p is string => Boolean(p))),
      ].sort(),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- the Map is mutable; staticVersion marks its changes
    [staticVersion],
  );
  const q = new URLSearchParams({ hours: String(hours), by: 'client' });
  if (protocol) q.set('protocol', protocol);
  const history = useQuery({
    queryKey: ['stats', 'presence', protocol, hours],
    queryFn: () =>
      request('GET', `/api/v1/stats/presence?${q.toString()}`, undefined, (v) =>
        api.PresenceHistory.parse(v),
      ),
    refetchInterval: 60_000,
  });
  const clients = history.data?.clients ?? [];
  const selected = clients.find((c) => c.client_id === client);
  const points = selected ? selected.points : (history.data?.points ?? []);

  return (
    <Card
      title={
        selected
          ? `${t('dashboard.onlineOverTime')} · ${selected.name}`
          : t(clientUser ? 'dashboard.onlineOverTime' : 'dashboard.onlineOverTimeTotal')
      }
      actions={
        <span className="toolbar chart-filters">
          {/* the authority observer (no operations.view) sees its own client only: no selector; other roles unchanged */}
          {ops && (
            <select
              className="input"
              value={client}
              onChange={(e) => setClient(e.target.value)}
              aria-label={t('common.site')}
            >
              <option value="">{t('dashboard.allClients')}</option>
              {clients.map((c) => (
                <option key={c.client_id} value={c.client_id}>
                  {c.name}
                </option>
              ))}
            </select>
          )}
          {ops && (
            <select
              className="input"
              value={protocol}
              onChange={(e) => setProtocol(e.target.value)}
              aria-label={t('devices.columns.protocol')}
            >
              <option value="">{t('devices.filters.allProtocols')}</option>
              {protocols.map((p) => (
                <option key={p} value={p}>
                  {t(`catalog.protocol.${p}` as 'common.unknown' /* any profile key */, { defaultValue: p })}
                </option>
              ))}
            </select>
          )}
          <select
            className="input"
            value={hours}
            onChange={(e) => setHours(Number(e.target.value))}
            aria-label={t('dashboard.range')}
          >
            {RANGES.map((h) => (
              <option key={h} value={h}>
                {h < 24 ? t('dashboard.lastHours', { count: h }) : t('dashboard.lastDays', { count: h / 24 })}
              </option>
            ))}
          </select>
        </span>
      }
    >
      {points.length < 2 ? (
        <p className="muted" style={{ margin: 0 }}>
          {history.isLoading ? t('common.loading') : t('dashboard.chartWaiting')}
        </p>
      ) : (
        <LineChart points={points} />
      )}
      {clients.length > 0 && (
        <ClientTable
          clients={clients}
          selected={client}
          onSelect={(id) => setClient(id === client ? '' : id)}
        />
      )}
    </Card>
  );
}

function LineChart({ points }: { points: Point[] }) {
  const { t } = useTranslation();
  const [box, W] = useWidth<HTMLDivElement>();
  const [hover, setHover] = useState<number | null>(null);
  const t0 = new Date(points[0]!.t).getTime();
  const t1 = new Date(points.at(-1)!.t).getTime();
  const yMax = niceMax(Math.max(...points.map((p) => Math.max(p.online, p.offline)), 1));
  const plotW = Math.max(W - M.left - M.right, 10);
  const x = (ts: string) => M.left + ((new Date(ts).getTime() - t0) / Math.max(t1 - t0, 1)) * plotW;
  const y = (v: number) => M.top + (1 - v / yMax) * (H - M.top - M.bottom);
  const path = (key: 'online' | 'offline') =>
    points.map((p, i) => `${i ? 'L' : 'M'}${x(p.t).toFixed(1)},${y(p[key]).toFixed(1)}`).join('');
  const ticks = [0, 0.25, 0.5, 0.75, 1].map((f) => Math.round(yMax * f));
  const long = t1 - t0 > 36 * 3600_000;
  const timeFmt = new Intl.DateTimeFormat(
    i18n.language,
    long ? { weekday: 'short', day: 'numeric' } : { hour: '2-digit', minute: '2-digit' },
  );
  // X labels: at most one per ~110 px, never two with the same text.
  const xTicks: Point[] = [];
  const nTicks = Math.max(2, Math.min(8, Math.floor(plotW / 110)));
  for (let i = 0; i < nTicks; i++) {
    const p = points[Math.round((i * (points.length - 1)) / (nTicks - 1))]!;
    if (!xTicks.some((q) => timeFmt.format(new Date(q.t)) === timeFmt.format(new Date(p.t)))) xTicks.push(p);
  }
  const last = points.at(-1)!;
  const labelsClash = Math.abs(y(last.online) - y(last.offline)) < 14;

  const onMove = (e: PointerEvent<SVGSVGElement>) => {
    const r = e.currentTarget.getBoundingClientRect();
    const px = e.clientX - r.left;
    let best = 0;
    for (let i = 1; i < points.length; i++)
      if (Math.abs(x(points[i]!.t) - px) < Math.abs(x(points[best]!.t) - px)) best = i;
    setHover(best);
  };
  const h = hover !== null ? points[hover]! : null;

  return (
    <div className="line-chart" ref={box}>
      <div className="chart-legend">
        <span>
          <i className="swatch swatch-online" /> {t('presence.online')}
        </span>
        <span>
          <i className="swatch swatch-offline" /> {t('presence.offline')}
        </span>
      </div>
      {W > 0 && (
        <svg
          width={W}
          height={H}
          role="img"
          aria-label={t('dashboard.onlineOverTime')}
          onPointerMove={onMove}
          onPointerLeave={() => setHover(null)}
        >
          {ticks.map((v) => (
            <g key={v}>
              <line className="grid" x1={M.left} x2={W - M.right} y1={y(v)} y2={y(v)} />
              <text className="axis" x={M.left - 8} y={y(v) + 4} textAnchor="end">
                {formatNumber(v)}
              </text>
            </g>
          ))}
          {xTicks.map((p, i) => (
            <text
              key={p.t}
              className="axis"
              x={x(p.t)}
              y={H - 8}
              textAnchor={i === 0 ? 'start' : i === xTicks.length - 1 ? 'end' : 'middle'}
            >
              {timeFmt.format(new Date(p.t))}
            </text>
          ))}
          <path className="series series-offline" d={path('offline')} />
          <path className="series series-online" d={path('online')} />
          <text className="end-label" x={x(last.t) + 6} y={y(last.online) + (labelsClash ? -4 : 4)}>
            {formatNumber(last.online)}
          </text>
          <text
            className="end-label muted-label"
            x={x(last.t) + 6}
            y={y(last.offline) + (labelsClash ? 12 : 4)}
          >
            {formatNumber(last.offline)}
          </text>
          {h && (
            <g>
              <line className="crosshair" x1={x(h.t)} x2={x(h.t)} y1={M.top} y2={H - M.bottom} />
              <circle className="dot dot-online" cx={x(h.t)} cy={y(h.online)} r={4} />
              <circle className="dot dot-offline" cx={x(h.t)} cy={y(h.offline)} r={4} />
            </g>
          )}
        </svg>
      )}
      {h && (
        <div className="chart-tooltip" style={{ left: Math.min(Math.max(x(h.t), 90), W - 90) }}>
          <strong>
            {new Intl.DateTimeFormat(i18n.language, { dateStyle: 'short', timeStyle: 'short' }).format(
              new Date(h.t),
            )}
          </strong>
          <span>
            <i className="swatch swatch-online" /> {t('presence.online')}: {formatNumber(h.online)}
          </span>
          <span>
            <i className="swatch swatch-offline" /> {t('presence.offline')}: {formatNumber(h.offline)}
          </span>
          <span className="muted">
            {t('presence.unknown')}: {formatNumber(h.unknown)}
          </span>
        </div>
      )}
    </div>
  );
}

/** Online count per client: current value, share of the client's devices, sparkline of online over the range. */
function ClientTable({
  clients,
  selected,
  onSelect,
}: {
  clients: NonNullable<api.PresenceHistory['clients']>;
  selected: string;
  onSelect: (id: string) => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="client-rows" role="table" aria-label={t('dashboard.perClient')}>
      <div className="client-row client-row-head" role="row">
        <span>{t('dashboard.client')}</span>
        <span className="num">{t('presence.online')}</span>
        <span className="num">{t('dashboard.devicesTotal')}</span>
        <span>{t('dashboard.trend')}</span>
      </div>
      {clients.map((c) => {
        const last = c.points.at(-1);
        const total = last ? last.online + last.offline + last.unknown : 0;
        return (
          <button
            key={c.client_id}
            type="button"
            role="row"
            className="client-row"
            aria-pressed={selected === c.client_id}
            onClick={() => onSelect(c.client_id)}
          >
            <span className="ellipsis">{c.name}</span>
            <span className="num strong">{formatNumber(last?.online ?? 0)}</span>
            <span className="num muted">{formatNumber(total)}</span>
            <Sparkline values={c.points.map((p) => p.online)} max={Math.max(total, 1)} />
          </button>
        );
      })}
    </div>
  );
}

function Sparkline({ values, max }: { values: number[]; max: number }) {
  const w = 120;
  const h = 22;
  if (values.length < 2) return <span />;
  const d = values
    .map(
      (v, i) =>
        `${i ? 'L' : 'M'}${((i / (values.length - 1)) * w).toFixed(1)},${(h - 2 - (v / max) * (h - 4)).toFixed(1)}`,
    )
    .join('');
  return (
    <svg width={w} height={h} className="sparkline" aria-hidden>
      <path d={d} />
    </svg>
  );
}
